import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Avatar, Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListUsersQuery } from "store/services/userApi";

import { useAuth, useAppMeta, useTablePagination } from "hooks";

import { USER_TYPES } from "data/static/staticOptions";
import ExportUsers from "./userComponents/userExcelDownload";

const UserListScreen = () => {
  const { isEntityAdmin, isSystemAdmin, isAccountAdmin } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "System";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Users",
    },
  ];

  const userListTabs = [
    ...USER_TYPES.map((type) => ({ label: type, value: type })),
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      cell: (row) => (
        <Avatar src={row?.avatar?.url} sx={{ width: 30, height: 30 }} />
      ),
      width: "50px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.name}
          to={`/app/crm/user/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <a
          href={`mailto:${row?.email}`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.email ? (
            <HighlightedText text={row?.email} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    ...(!isEntityAdmin
      ? [
        {
          name: "Entities",
          cell: (row) => (
            <ul className="py-1">
              {row?.entities?.slice(0, 2).map((entity) => (
                <li
                  key={entity?._id}
                  className="list-disc text-sm text-blue-500 line-clamp-3 dark:text-blue-200"
                >
                  <span>●</span> {entity?.displayName ?? entity?.name}
                </li>
              ))}

              {row?.entities?.length > 2 && (
                <li className="ml-3 text-xs text-gray-500 dark:text-gray-200">
                  (+ {row?.entities?.length - 2} more)
                </li>
              )}
            </ul>
          ),
        },
      ]
      : []),
    {
      name: "Designation",
      selector: (row) => row?.designation ?? "N/A",
    },
    ...(activeTab === "Inactive"
      ? [
        {
          name: "Type",
          selector: (row) => row?.typeRefSource ?? "System",
        },
      ]
      : []),
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/user/${row._id}/detail`}>
            <IconButton size="small" aria-label="View User" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/user/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit User">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // RTK Query
  const [
    fetchUsers,
    {
      data: userRes,
      isLoading: loadingUsers,
      isFetching: fetchingUsers,
      error,
    },
  ] = useLazyListUsersQuery();

  React.useEffect(() => {
    fetchUsers({
      active: activeTab !== "Inactive",
      ...(!["Inactive"].includes(activeTab) && {
        typeRefSource: activeTab,
      }),
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchUsers, keyword, perPage]);

  useAppMeta(`${activeTab} Users`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={
        ["System", "Referral", "Inactive"].includes(activeTab) && (
          <PageActions />
        )
      }
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={userListTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Name/Email"
          disabled={loadingUsers || fetchingUsers}
          exportComponent={
            (isSystemAdmin || isAccountAdmin)
              ? () => <ExportUsers isBusy={loadingUsers || fetchingUsers} />
              : null
          }
        >
          <div className="font-medium"> {activeTab} </div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={userRes?.data ?? []}
          progressPending={loadingUsers || fetchingUsers}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={userRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default UserListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/user/create">
      <Button startIcon={<Add />} variant="contained">
        New User
      </Button>
    </Link>
  );
};
